import React, { useState, useEffect, useRef } from 'react';
import { Check, GitBranch } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/apiService';
import { accountUrl } from '../../services/oidcService';
import { setSettingsModalOpen, setActiveTab as openAppTab } from '../../store/uiModalStore';
import { Modal } from '../ui/Modal';
import { openExternal } from '../../utils/openExternal';
import { useAvatarStore } from '../../store/avatarStore';
import UserAvatar from '../ui/UserAvatar';
import AvatarStatusBadge from '../ui/AvatarStatusBadge';
import VaultHandoffSection from '../VaultHandoffSection';
import { Row, Section } from '../settingsPrimitives';
import { PasswordInput, PasswordStrengthMeter } from './PasswordInput';
import { AccountDataSection } from './AccountDataSection';

export const AccountTab = ({ t, showToast }) => {
  const { user, updateProfile } = useAuthStore();

  // ── Profile state ────────────────────────────────────────────────────────────
  // Name and avatar are owned by the IdP; Settings only links out to the hosted
  // account page and re-reads the profile when the window regains focus.
  const bumpAvatarVersion = useAvatarStore((s) => s.bumpAvatarVersion);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordState, setPasswordState] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [passwordError, setPasswordError] = useState('');
  const passwordSaving = passwordState === 'saving';

  // ── CLI token state ──────────────────────────────────────────────────────────
  const [cliToken, setCliToken] = useState(null);
  const [cliBusy, setCliBusy] = useState(false);
  const [cliCopied, setCliCopied] = useState(false);
  const cliCopyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (cliCopyTimeoutRef.current) clearTimeout(cliCopyTimeoutRef.current);
    };
  }, []);

  const handleGenerateCliToken = async () => {
    setCliBusy(true);
    setCliCopied(false);
    try {
      const res = await authService.createCliToken();
      setCliToken(res?.token || null);
      if (!res?.token) showToast?.(t('settings.generateCliTokenFailed'), 'error');
    } catch (error) {
      showToast?.(error.response?.data?.message || t('settings.generateCliTokenFailed'), 'error');
    } finally {
      setCliBusy(false);
    }
  };

  const handleCopyCliToken = async () => {
    if (!cliToken) return;
    try {
      await navigator.clipboard.writeText(cliToken);
      setCliCopied(true);
      if (cliCopyTimeoutRef.current) clearTimeout(cliCopyTimeoutRef.current);
      cliCopyTimeoutRef.current = setTimeout(() => setCliCopied(false), 1500);
    } catch {
      showToast?.(t('settings.copyCliTokenFailed'), 'error');
    }
  };

  const openAccountPage = (tab) => {
    const url = tab ? `${accountUrl()}?tab=${tab}` : accountUrl();
    openExternal(url);
  };

  // The name/picture change happens in the browser, so pick up the new profile
  // when the user comes back to the app.
  useEffect(() => {
    const onFocus = () => {
      authService.getUser()
        .then((fresh) => { useAuthStore.setState({ user: fresh }); bumpAvatarVersion(); })
        .catch(() => { /* ignore */ });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [bumpAvatarVersion]);

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) { setPasswordError('Current password is required.'); return; }
    if (!newPassword) { setPasswordError('New password is required.'); return; }
    if (newPassword.length < 8) { setPasswordError('New password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setPasswordError("Passwords don't match."); return; }
    setPasswordError('');
    setPasswordState('saving');
    try {
      const result = await updateProfile({
        name: user?.name, email: user?.email,
        current_password: currentPassword, new_password: newPassword,
      });
      if (result.success) {
        setPasswordState('saved');
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordState('idle');
          setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
          showToast?.('Password changed successfully.', 'success');
        }, 1000);
      } else {
        setPasswordError(result.error || 'Failed to change password.');
        setPasswordState('idle');
      }
    } catch {
      setPasswordError('Failed to change password.');
      setPasswordState('idle');
    }
  };

  return (
    <>
      {/* ── Profile ─────────────────────────────────────────────────────────── */}
      <Section title="Profile">
        <Row label="Avatar" desc="Your picture is managed on your Apiboo account page.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <UserAvatar userId={user?.id} name={user?.name || 'U'} size={32}>
              <AvatarStatusBadge userId={user?.id} avatarSize={32} />
            </UserAvatar>
            <button
              type="button"
              className="settings-action-btn flex-shrink-0"
              onClick={() => openAccountPage('profile')}
            >
              Change photo
            </button>
          </div>
        </Row>
        <Row label="Full name" desc="Your name is managed on your Apiboo account page.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="settings-row-desc" style={{ margin: 0 }}>{user?.name}</span>
            <button
              type="button"
              className="settings-action-btn flex-shrink-0"
              onClick={() => openAccountPage('profile')}
            >
              Change name
            </button>
          </div>
        </Row>
        <Row label="Email">
          <span className="settings-row-desc" style={{ margin: 0 }}>{user?.email}</span>
        </Row>
      </Section>

      <Section title="Password">
        <Row label="Password" desc="Passwords are managed on your Apiboo account page.">
          <button
            type="button"
            className="settings-action-btn flex-shrink-0"
            onClick={() => openAccountPage()}
          >
            Open account page
          </button>
        </Row>
      </Section>

      <Section title={t('settings.terminalAccess')}>
        <Row
          label={t('settings.cliToken')}
          desc={t('settings.cliTokenDesc')}
        >
          <button
            type="button"
            className="settings-action-btn flex-shrink-0"
            disabled={cliBusy}
            onClick={handleGenerateCliToken}
          >
            {cliBusy ? t('settings.generatingEllipsis') : cliToken ? t('settings.regenerateToken') : t('settings.generateToken')}
          </button>
        </Row>
        {cliToken && (
          <div className="flex items-center gap-2" style={{ marginTop: 8 }}>
            <code
              className="flex-1 truncate rounded-md px-2 py-1.5 text-xs"
              style={{ background: 'var(--bg-tertiary, rgba(127,127,127,0.12))', userSelect: 'all' }}
              title={cliToken}
            >
              {cliToken}
            </code>
            <button
              type="button"
              className="settings-action-btn flex-shrink-0"
              onClick={handleCopyCliToken}
            >
              {cliCopied ? t('settings.copied', 'Copied') : t('settings.copy', 'Copy')}
            </button>
          </div>
        )}
        {cliToken && (
          <div className="settings-row-desc" style={{ marginTop: 6 }}>
            {t('settings.cliTokenOnceNotice')}
          </div>
        )}
      </Section>

      <Modal
        isOpen={showPasswordModal}
        onClose={() => { if (!passwordSaving) { setShowPasswordModal(false); setPasswordError(''); } }}
        title="Change password"
        preventBackdropClose={passwordSaving}
        preventEscapeClose={passwordSaving}
      >
        <form onSubmit={handleSavePassword} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--text-2, #a1a1aa)' }}>Current password</label>
            <PasswordInput
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(''); }}
              autoComplete="current-password"
              disabled={passwordSaving}
              autoFocus
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--text-2, #a1a1aa)' }}>New password</label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPasswordError(''); }}
              autoComplete="new-password"
              disabled={passwordSaving}
              style={{ width: '100%' }}
            />
            <PasswordStrengthMeter password={newPassword} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--text-2, #a1a1aa)' }}>Confirm new password</label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(''); }}
              autoComplete="new-password"
              disabled={passwordSaving}
              style={{ width: '100%' }}
            />
          </div>
          {passwordError && <p className="text-xs" style={{ color: 'var(--danger, #ef4444)', margin: 0 }}>{passwordError}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button
              type="button"
              className="settings-action-btn"
              onClick={() => { setShowPasswordModal(false); setPasswordError(''); }}
              disabled={passwordSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="settings-action-btn"
              disabled={passwordState !== 'idle' || !currentPassword || !newPassword || !confirmPassword}
              style={{ minWidth: 72 }}
            >
              {passwordState === 'saving' && <span className="env-save-spinner" />}
              {passwordState === 'saved' && <Check size={15} color="#4ade80" strokeWidth={2.5} />}
              {passwordState === 'idle' && 'Change'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Security ─────────────────────────────────────────────────────────── */}
      {/* Two-factor and passkeys are managed at the central IdP (auth.apiboo.com)
          — WebAuthn ceremonies need a real browser origin anyway. */}
      <Section title="Security">
        <Row
          label="Two-factor & passkeys"
          desc="Authenticator app, backup codes, passkeys and active sessions are managed on your Apiboo account page."
        >
          <button
            type="button"
            className="settings-action-btn flex-shrink-0"
            onClick={() => openAccountPage()}
          >
            Manage security
          </button>
        </Row>
      </Section>

      {/* ── Git accounts: on the Git page since git sync card 8 ──────────────── */}
      <Section title={t('settings.accountsGit', 'Git providers')}>
        <Row label="Git accounts" desc="GitHub, GitLab and other git servers are signed in on the Git page.">
          <button
            type="button"
            className="settings-action-btn"
            onClick={() => {
              setSettingsModalOpen(false);
              openAppTab('git');
            }}
          >
            <GitBranch size={13} aria-hidden /> Open Git page
          </button>
        </Row>
      </Section>

      {/* ── Local data hand-off (opt-in, REQ-local-to-account-handoff.md) ────── */}
      <VaultHandoffSection />

      {/* ── Data & Account Deletion ───────────────────────────────────────────── */}
      <AccountDataSection t={t} />
    </>
  );
};
